import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sso } from '../../../auth/sso';

// Fail-safe error copy: friendly, retryable copy per failure reason — raw vendor text
// never surfaces. "Try signing in again" restarts the authorize leg (beginLogin);
// the single-use code is never re-POSTed.
const REASON_COPY = {
  provider_error: 'Sign-in was cancelled or denied. Please try again.',
  invalid_state: 'Your sign-in session expired or could not be verified. Please sign in again.',
  missing_code: 'The sign-in response was incomplete. Please sign in again.',
  exchange_failed: 'We couldn’t complete sign-in just now. Please try again in a moment.',
};

function parseSearch() {
  const p = new URLSearchParams(window.location.search);
  return {
    code: p.get('code') ?? undefined,
    state: p.get('state') ?? undefined,
    error: p.get('error') ?? undefined,
    error_description: p.get('error_description') ?? undefined,
  };
}

/**
 * Top-level /oauth/callback handler (NOT under ProtectedRoute — it runs before
 * any session exists). Runs handleOAuthCallback exactly once (StrictMode-safe run-once
 * ref: the authorization code is single-use). Success → navigate to the state-preserved
 * destination. Error → retryable card.
 */
export function OAuthCallback() {
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;

    sso
      .handleOAuthCallback(parseSearch())
      .then((outcome) => {
        if (outcome.status === 'success') {
          // destination is the pre-login location; it may be a full URL (the guard
          // captured window.location.href). React Router navigate() wants a path, so
          // reduce it to pathname+search+hash (same-origin only, never an open redirect).
          let dest = outcome.destination || '/';
          try {
            const u = new URL(dest, window.location.origin);
            dest = u.origin === window.location.origin ? u.pathname + u.search + u.hash : '/';
          } catch {
            /* already a path */
          }
          navigate(dest, { replace: true });
        } else {
          setError(outcome.reason || 'exchange_failed');
        }
      })
      .catch(() => setError('exchange_failed'));
  }, [navigate]);

  if (!error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 1rem' }}>
        <p>Signing you in…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 460, margin: '4rem auto', textAlign: 'center', padding: '0 1rem' }}>
      <h2>Sign-in Failed</h2>
      <p>{REASON_COPY[error] || REASON_COPY.exchange_failed}</p>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          sso
            .beginLogin('/')
            .then((url) => window.location.assign(url))
            .catch(() => window.location.assign('/'));
        }}
      >
        Try signing in again
      </button>
    </div>
  );
}

export default OAuthCallback;
